// @ts-nocheck

import axios from "axios";
import {
  BACKEND_API_BASE_URL,
  KNOWLEDGE_BASE_API_PATH,
  KNOWLEDGE_BASE_MAX_FILE_SIZE_BYTES,
  CLIENT_SOURCE_HEADER,
  CLIENT_SOURCE,
} from "../utilities/constant";
import { getUserAccessToken } from "../utilities/setting";


const kbClient = axios.create({
  baseURL: `${BACKEND_API_BASE_URL}${KNOWLEDGE_BASE_API_PATH}`,
  timeout: 60000,
});


function authHeaders(extra = {}) {
  return {
    Authorization: getUserAccessToken(),
    [CLIENT_SOURCE_HEADER]: CLIENT_SOURCE,
    ...extra,
  };
}

function extractErrorMessage(error) {
  if (!error) return "Something went wrong. Please try again.";
  if (error.response === undefined) {
    return `Cannot reach the server. Confirm your connection and try again.`;
  }

  const detail = error.response?.data?.error?.explanation || error.response?.data?.detail;
  if (Array.isArray(detail)) {
    return detail
      .map((item) => item?.msg || item?.type)
      .filter(Boolean)
      .join(". ") || "Validation failed.";
  }
  if (typeof detail === "string" && detail.trim()) {
    return detail;
  }
  if (typeof error.response?.data?.message === "string") {
    return error.response.data.message;
  }
  return `Request failed (${error.response?.status || "unknown"}).`;
}

function assertFileSize(file) {
  if (file && file.size > KNOWLEDGE_BASE_MAX_FILE_SIZE_BYTES) {
    const limitMb = (KNOWLEDGE_BASE_MAX_FILE_SIZE_BYTES / 1024 / 1024).toFixed(0);
    throw `File exceeds the ${limitMb} MB upload limit.`;
  }
}

/**
 * POST /v2/knowledge-bases  
 */
export const createKnowledgeBase = async ({
  kbName,
  userId,
  kbType,
  resellerId,
  url,
  file,
}) => {
  assertFileSize(file);
  const formData = new FormData();
  formData.append("kb_name", kbName);
  formData.append("user_id", userId);
  if (kbType) formData.append("kb_type", kbType);
  if (resellerId) formData.append("reseller_id", resellerId);
  if (url) formData.append("url", url);
  if (file) formData.append("file", file);

  try {
    const response = await kbClient.post("/", formData, {
      headers: authHeaders({ "Content-Type": "multipart/form-data" }),
    });
    return response.data?.data ?? response.data;
  } catch (error) {
    return Promise.reject(extractErrorMessage(error));
  }
};

/**
 * POST /v2/knowledge-bases/{kb_id}/documents 
 */
export const addKnowledgeBaseDocument = async (kbId, { url, file }) => {
  assertFileSize(file);
  const formData = new FormData();
  if (url) formData.append("url", url);
  if (file) formData.append("file", file);

  try {
    const response = await kbClient.post(
      `/${encodeURIComponent(kbId)}/documents`,
      formData,
      { headers: authHeaders({ "Content-Type": "multipart/form-data" }) },
    );
    return response.data?.data ?? response.data;
  } catch (error) {
    return Promise.reject(extractErrorMessage(error));
  }
};

/** GET /v2/knowledge-bases/{kb_id}/status */
export const getKnowledgeBaseStatus = async (kbId) => {
  try {
    const response = await kbClient.get(`/${encodeURIComponent(kbId)}/status`, {
      headers: authHeaders(),
    });
    return response.data?.data ?? response.data;
  } catch (error) {
    return Promise.reject(extractErrorMessage(error));
  }
};

/** GET /v2/knowledge-bases */
export const listKnowledgeBases = async () => {
  try {
    const response = await kbClient.get("/", { headers: authHeaders() });
    const data = response.data?.data ?? response.data;
    if (Array.isArray(data)) return data;
    if (Array.isArray(data?.knowledge_bases)) return data.knowledge_bases;
    if (Array.isArray(data?.data)) return data.data;
    if (Array.isArray(data?.items)) return data.items;
    return [];
  } catch (error) {
    return Promise.reject(extractErrorMessage(error));
  }
};

/** GET /v2/knowledge-bases/{kb_id} */
export const getKnowledgeBaseDetails = async (kbId) => {
  try {
    const response = await kbClient.get(`/${encodeURIComponent(kbId)}`, {
      headers: authHeaders(),
    });
    return response.data?.data ?? response.data;
  } catch (error) {
    return Promise.reject(extractErrorMessage(error));
  }
};

/** DELETE /v2/knowledge-bases/{kb_id} */
export const deleteKnowledgeBase = async (kbId) => {
  try {
    const response = await kbClient.delete(`/${encodeURIComponent(kbId)}`, {
      headers: authHeaders(),
    });
    return response.data?.data ?? response.data;
  } catch (error) {
    return Promise.reject(extractErrorMessage(error));
  }
};

/** GET /v2/knowledge-bases/check-url?url=...
 */
export const checkUrlReachable = async (url) => {
  try {
    const response = await kbClient.get("/check-url", {
      headers: authHeaders(),
      params: { url },
    });
    const data = response.data?.data ?? response.data;
    return { reachable: Boolean(data?.reachable) };
  } catch (error) {
    return Promise.reject(extractErrorMessage(error));
  }
};
